import React, { useState, useEffect, memo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

import dayjs from 'dayjs';

import { useStore } from 'src/context/StoreContext';
import {
  defaultHeaderNotDoctor,
  defaultHeaderOP,
  defaultBodyContentMeeting,
  stickyHeader,
  stickyCol,
  fontNote,
  sizeColumn
} from 'src/components/calendar/styles';
import { DataCalendarType, DatePrintType } from 'src/components/calendar/TypeCustom';

const colorBorderTable = '#DDDDDD';

type PrintDateCalendarProps = {
  dataCalendar: DataCalendarType;
  dataPrint: DatePrintType;
  onComplate: () => void;
};

type IndexDayType = {
  startIndex: number;
  endIndex: number;
};

const PrintDateCalendar: React.FC<PrintDateCalendarProps> = (props) => {
  const storeContext = useStore();
  const store = storeContext.store;

  const [doctorsData, setDoctorsData] = useState<any[]>();
  const [dayView, setDayView] = useState<any>({});
  const [indexDay, setIndexDay] = useState<IndexDayType>();
  const printRefDate = useRef(null);
  const [isReadyPrintDate, setIsReadyPrintDate] = useState<boolean>(false);

  const getDayView = () => {
    let index = 0;
    if (props.dataCalendar.days) {
      index = props.dataCalendar.days.findIndex(
        item => item.day && dayjs(item.day).format('YYYY-MM-DD') === props.dataPrint.datePrint
      );

      if (index !== -1) {
        setDayView(props.dataCalendar.days[index]);
      }
    }

    return index;
  }

  const getIndexDay = () => {
    const indexDay = getDayView();
    const times = props.dataCalendar.times;

    if (times.length !== 0) {
      const firstRow = times[0]['columns'] ? times[0]['columns'] : [];

      const indexTime = getIndex(firstRow, indexDay);

      setIndexDay(indexTime);
    }
  }

  const getIndex = (data: any[], n: number): IndexDayType => {
    let daylastcolCount = 0;
    let startIndex = n === 0 ? 1 : -1;
    let endIndex = -1;

    for (let i = 0; i < data.length; i++) {
      if (data[i].daylastcol === 1) {
        daylastcolCount++;
        if (daylastcolCount === n) {
          startIndex = i + 1;
        } else if (daylastcolCount === n + 1) {
          endIndex = i;
          break;
        }
      }
    }

    return {
      startIndex: data[startIndex]
        && (data[startIndex]['index'] || data[startIndex]['index'] === 0) ? data[startIndex]['index'] : -1,
      endIndex: data[endIndex] 
        && (data[endIndex]['index'] || data[endIndex]['index'] === 0)  ? data[endIndex]['index'] : -1
    }
  }

  const getSubArray = (data: any[]): any[] => {
    const startIndex = indexDay && indexDay.startIndex !== undefined ? indexDay.startIndex : -1;
    const endIndex = indexDay && indexDay.endIndex !== undefined ? indexDay.endIndex : -1;
    let subArray: any[] = [];

    if (data.length !== 0) {
      if (startIndex !== -1 && endIndex !== -1) {
        subArray = data.filter(item => item.index !== undefined && item.index >= startIndex && item.index <= endIndex);

      } else if (startIndex !== -1 && endIndex === -1) {
        subArray = data.filter(item => item.index !== undefined && item.index >= startIndex);
      }
      subArray = [data[0], ...subArray];
    }

    return subArray;
  };

  const formatDoctorsData = () => {
    const doctors = props.dataCalendar.doctors;

    const doctorsObject: any = doctors ? doctors.reduce((acc: any, doctor: any) => {
      acc[doctor.id] = doctor;
      return acc;
    }, {} as any) : {};

    setDoctorsData(doctorsObject);
  }

  const getStyleDoctor = (doctorid: number) => {
    return {
      bgColor: doctorsData
        && doctorsData[doctorid]
        && doctorsData[doctorid]['bgcolor']
        ? doctorsData[doctorid]['bgcolor'] : '',
      color: doctorsData
        && doctorsData[doctorid]
        && doctorsData[doctorid]['fontcolor']
        ? doctorsData[doctorid]['fontcolor'] : ''
    }
  }

  const getNote = (day: string, type: number, index: number) => {
    const note = props.dataCalendar.notes
      && props.dataCalendar.notes[day]
      && props.dataCalendar.notes[day][type]
      && props.dataCalendar.notes[day][type][index]
      && props.dataCalendar.notes[day][type][index]['note']
      ? props.dataCalendar.notes[day][type][index]['note'] : '';

    return note;
  }

  const handlePrint = useReactToPrint({
    content: () => printRefDate.current,
  });

  useEffect(() => {
    if (props.dataCalendar.days.length !== 0) {
      formatDoctorsData();
      getIndexDay();
    }

    // eslint-disable-next-line
  }, [props.dataCalendar.days]);

  useEffect(() => {
    if (props.dataPrint.code !== '') {
      getIndexDay();
      setIsReadyPrintDate(true);
    }

    // eslint-disable-next-line
  }, [props.dataPrint.code]);

  useEffect(() => {
    if (isReadyPrintDate) {
      handlePrint();
      setIsReadyPrintDate(false);
      props.onComplate();
    }

    // eslint-disable-next-line
  }, [isReadyPrintDate]);

  return (
    <Box ref={printRefDate}>
      <Paper sx={{
        bgcolor: store.palette.container.paper,
        borderRadius: '16px',
        mt: '10px',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        '@media print': {
          borderRadius: 0,
          margin: 0,
          width: '100%',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}>
        <TableContainer
          sx={{
            boxShadow: 'none',
            '& *': { color: 'primary.dark', fontSize: '12px' },
            width: '500px',
            height: '100%',
            '@media print': {
              width: '100%',
              height: 'auto',
            },
          }}
        >
          <Table
            stickyHeader
            sx={{
              '& .MuiTableCell-root.MuiTableCell-head': {
                borderBottom: `1px solid ${colorBorderTable}`,
                borderRight: `3px solid ${colorBorderTable}`,
                fontWeight: 700,
                fontSize: '12px',
                p: '0px 8px',
                overflow: 'auto',
                '&:first-of-type': {
                  ...stickyCol,
                  zIndex: 3
                }
              },
              '& .MuiTableCell-root': {
                borderRight: `3px solid ${colorBorderTable}`,
              },
              '& .MuiTableCell-root.MuiTableCell-body': {
                p: '0px 8px',
                '&:first-of-type': {
                  ...stickyCol
                }
              }
            }}
            aria-label='simple table'
          >
            <TableHead>
              <TableRow sx={{ height: '52px', ...stickyHeader }}>
                <TableCell
                  align='center'
                />
                <TableCell
                  id={dayView['day']}
                  key={dayView['day']}
                  align='center'
                  colSpan={dayView['colspan']}
                >
                  <Box sx={{ mx: 'auto' }}>
                    <Typography
                      sx={{
                        fontWeight: '400',
                        lineHeight: '10px',
                        fontSize: '10px'
                      }}
                      component='pre'
                    >
                      月
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: '500',
                        lineHeight: '21.6px',
                        fontSize: '24px'
                      }}
                      component='pre'
                    >
                      {dayjs(dayView['day']).format('D')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>

              <TableRow sx={{ height: '36px', ...stickyHeader, top: '52px' }}>
                <TableCell
                  align='center'
                />
                <React.Fragment key={dayView['day']}>
                  {dayView['doctors'] && dayView['doctors'].map((doctor: any, indexDoctor: number) => (
                    <TableCell
                      key={dayView['day'] + '-doctor' + doctor['id']}
                      colSpan={2}
                      align='center'
                      sx={{
                        ...defaultHeaderNotDoctor,
                        bgcolor: getStyleDoctor(doctor['id']).bgColor,
                        color: getStyleDoctor(doctor['id']).color
                      }}
                    >
                      {doctor['name']}
                    </TableCell>
                  ))}
                  {dayView['machines'] && dayView['machines'].map((machine: any, indexMachine: number) => (
                    <TableCell
                      key={dayView['day'] + '-machine' + machine['id']}
                      sx={{
                        ...defaultHeaderNotDoctor
                      }}
                      align='center'
                    >
                      {machine['machine']}
                    </TableCell>
                  ))}
                  {dayView['nurse'] && dayView['nurse'].length !== 0 && (
                    <TableCell
                      key={dayView['day'] + 'nurse'}
                      align='center'
                      sx={{
                        ...defaultHeaderNotDoctor
                      }}
                    >
                      看護師
                    </TableCell>
                  )}
                  {dayView['reception'] && dayView['reception'].length !== 0 && (
                    <TableCell
                      key={dayView['day'] + 'reception'}
                      sx={{
                        ...defaultHeaderNotDoctor
                      }}
                      align='center'
                    >
                      受付
                    </TableCell>
                  )}
                </React.Fragment>
              </TableRow>

              <TableRow sx={{ height: '100px', ...stickyHeader, top: '88px' }}>
                <TableCell
                  align='center'
                  sx={{
                    width: '46px'
                  }}
                />
                <React.Fragment key={'note-' + dayView['day']}>
                  {dayView['doctors'] && dayView['doctors'].map((doctor: any, indexDoctor: number) => (
                    <TableCell
                      key={dayView['day'] + '-note-doctor-' + doctor['id']}
                      colSpan={2}
                      align='left'
                    >
                      <Typography
                        sx={{ ...fontNote }}
                        component='pre'
                      >
                        {getNote(dayView['day'], 1, doctor['id'])}
                      </Typography>
                    </TableCell>
                  ))}
                  {dayView['machines'] && dayView['machines'].map((machine: any, indexMachine: number) => (
                    <TableCell
                      key={dayView['day'] + '-note-machines-' + machine['id']}
                      rowSpan={2}
                      align='left'
                      sx={{
                        ...sizeColumn
                      }}
                    >
                      <Typography
                        sx={{ ...fontNote }}
                        component='pre'
                      >
                        {getNote(dayView['day'], 3, machine['id'])}
                      </Typography>
                    </TableCell>
                  ))}
                  {dayView['nurse'] && dayView['nurse'].map((nur: any, index: number) => (
                    <TableCell
                      key={dayView['day'] + '-note-nurse-' + nur}
                      rowSpan={2}
                      align='left'
                      sx={{
                        ...sizeColumn
                      }}
                    >
                      <Typography
                        sx={{ ...fontNote }}
                        component='pre'
                      >
                        {getNote(dayView['day'], 4, index)}
                      </Typography>
                    </TableCell>
                  ))}
                  {dayView['reception'] && dayView['reception'].map((reception: any, index: number) => (
                    <TableCell
                      key={dayView['day'] + '-note-reception-' + reception}
                      rowSpan={2}
                      align='left'
                      sx={{
                        ...sizeColumn
                      }}
                    >
                      <Typography
                        sx={{ ...fontNote }}
                        component='pre'
                      >
                        {getNote(dayView['day'], 5, index)}
                      </Typography>
                    </TableCell>
                  ))}
                </React.Fragment>
              </TableRow>

              <TableRow sx={{ height: '25px', ...stickyHeader, top: '188px' }}>
                <TableCell
                  align='center'
                />
                {dayView['doctors'] && dayView['doctors'].map((doctor: any) => (
                  <React.Fragment key={dayView['day'] + doctor['id']}>
                    <TableCell
                      key={dayView['day'] + '-op-' + doctor['id']}
                      sx={{ ...sizeColumn, ...defaultHeaderOP, height: '25px' }}
                      align='center'
                    >
                      OP
                    </TableCell>
                    <TableCell
                      key={dayView['day'] + '-OP以外-' + doctor['id']}
                      sx={{ ...sizeColumn, ...defaultHeaderOP, height: '25px' }}
                      align='center'
                    >
                      OP以外
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {props.dataCalendar.times && props.dataCalendar.times.map((time: any, index: number) => (
                <TableRow
                  hover
                  sx={{ height: '30px' }}
                  key={`${time['hm']}-${index}`}
                >
                  {time && time['columns'] && getSubArray(time['columns']).map((column: any, index: number) => (
                    index === 0 ? (
                      <TableCell
                        key={`${time['hm']}-${index}`}
                        align='left'
                        sx={{
                          fontSize: '12px',
                          fontWeight: 400,
                          lineWeight: '14.4px',
                          '&.MuiTableCell-body': {
                            borderBottom: '0px',
                            borderTop: time['show'] ? `1px solid ${colorBorderTable}` : '0px'
                          }
                        }}
                      >
                        {column['text'][0]['label']}
                      </TableCell>
                    ) : (
                      column['text'] ? (
                        <TableCell
                          key={time['hm'] + column['index']}
                          align='left'
                          rowSpan={column['rowspan'] ? column['rowspan'] : 6}
                          sx={{
                            ...sizeColumn,
                            height: `${column['rowspan'] * 30}px`,
                            verticalAlign: 'top',
                            bgcolor: column['color']['bg'],
                            '&.MuiTableCell-body': {
                              borderBottom: '0px',
                              borderTop: time['show'] ? '1px solid #DDDDDD' : '0px'
                            }
                          }}
                        >
                          <Box sx={{ py: '6px', height: `${column['rowspan'] * 30}px` }}>
                            <Stack
                              sx={{
                                width: '100%',
                                height: '100%',
                                bgcolor: column['color']['bg'],
                                p: '6px',
                                borderRadius: '4px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                minHeight: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {column['text'].map((text: any) => (
                                <React.Fragment key={column['index'] + text['value']}>
                                  {(text['value'] === 'fullname') && (
                                    <Typography
                                      sx={{ ...defaultBodyContentMeeting }}
                                      component='div'
                                      dangerouslySetInnerHTML={{ __html: text['label'] }}
                                    />
                                  )}
                                  {(text['value'] === 'patientname') && (
                                    <Typography
                                      sx={{ ...defaultBodyContentMeeting }}
                                      component='div'
                                      dangerouslySetInnerHTML={{ __html: text['label'] }}
                                    />
                                  )}
                                  {(text['value'] === 'icons') && (
                                    <Typography
                                      sx={{ ...defaultBodyContentMeeting }}
                                      component='div'
                                      dangerouslySetInnerHTML={{ __html: text['label'] }}
                                    />
                                  )}
                                  {(text['value'] === 'visittime') && (
                                    <Typography
                                      sx={{ ...defaultBodyContentMeeting }}
                                      component='label'
                                    >{text['label']}</Typography>
                                  )}
                                  {(text['value'] === 'category') && (
                                    <Typography
                                      sx={{ ...defaultBodyContentMeeting }}
                                      component='label'
                                    >{text['label']}</Typography>
                                  )}
                                  {(text['value'].includes('note')) && (
                                    <Typography
                                      sx={{ ...defaultBodyContentMeeting }}
                                      component='label'
                                    >{text['label']}</Typography>
                                  )}
                                </React.Fragment>
                              ))}
                            </Stack>
                          </Box>
                        </TableCell>

                      ) : (
                        <TableCell
                          key={time['hm'] + column['index']}
                          align='left'
                          sx={{
                            '&.MuiTableCell-body': {
                              borderBottom: '0px',
                              borderTop: time['show'] ? '1px solid #DDDDDD' : '0px'
                            }
                          }}
                        />
                      )
                    )
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
const PrintDateCalendarMemo = memo(PrintDateCalendar);

export default PrintDateCalendarMemo;